import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useLanguage from "../../hooks/useLanguage.js";
import {
  trackCTA,
  trackLead,
  trackSectionViewed,
  useSectionOnceObserver,
} from "../../lib/analytics";
import { buildSandboxUrl } from "../../lib/sandboxDemo.js";
import { WPL_SECTION } from "./sections.js";

/**
 * "A planning hub you can open right now."
 *
 * Deliberately the only framed, centred block on a page of left-aligned
 * editorial sections — the same treatment `InvitationDemo` gets on
 * /digital-wedding-invitation, and for the same reason: on a page of
 * statements, the one thing that is an OFFER should not look like another
 * paragraph. It reuses that page's `dwi-demo` classes rather than minting a
 * second identical block, which is also why this page carries `dwi-page`.
 *
 * No photograph here on purpose, exactly as on the invitation page.
 *
 * The claim boundary this section must never cross: the sandbox is a real
 * Heirloom event with a seeded Planning Hub, but Éros is blocked inside it
 * (`blockSandboxAssistant` in api/routes/assistant.routes.mjs). The note says
 * so. A visitor who opens the demo expecting the assistant and cannot find it
 * has been mis-sold by this section, not by the product.
 */
const PlanningDemo = () => {
  const { t } = useTranslation();
  const { shortLanguage } = useLanguage();
  const analyticsContext = useAnalyticsContext();

  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(WPL_SECTION.DEMO, analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section topless wps-section">
      <div className="container">
        <div className="dwi-demo">
          <p className="wps-eyebrow">{t("weddingPlanning.demo.eyebrow")}</p>
          <h2 className="heading-50">{t("weddingPlanning.demo.heading")}</h2>
          <p className="body-20 dwi-demo-body">
            {t("weddingPlanning.demo.body")}
          </p>
          <div className="dwi-demo-action">
            <Button
              href={buildSandboxUrl(shortLanguage)}
              target="_blank"
              rel="nofollow"
              text={t("weddingPlanning.demo.cta")}
              className="button-lg"
              onClick={() => {
                const ctx = { ...analyticsContext, ctaName: "start_trial" };
                trackCTA("wedding_planning_demo", ctx);
                trackLead("wedding_planning_demo", ctx);
              }}
            />
          </div>
          <p className="wps-note dwi-demo-note">
            {t("weddingPlanning.demo.note")}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PlanningDemo;
